import { forwardRef, useEffect, useState } from 'react';
import PureModal from 'react-pure-modal';
import { useNavigate } from 'react-router-dom';
import UIkit from 'uikit';
import { api_config } from '../../api/api-commons';
import type { ApiResource } from '../../api/services/config/resource/IResourceService';
import resourceService from '../../api/services/config/resource/ResourceService';
import type { ApiReturnResource } from '../../api/services/config/tags/ITagService';
import type { ApiTag } from '../../api/services/config/tags/TagService';
import TagService from '../../api/services/config/tags/TagService';
import { includeByName } from '../../utils/functions';
import { createValidator, useForm } from '../../utils/hooks/useForm';
import FilePicker from '../utils/filePicker/FilePicker';
import TagInput from '../utils/tagInput/TagInput';
import TextInputComponent from '../utils/textInput/TextInputComponent';

// Props.
interface EditResourceProps {
  handleClose: (value: boolean) => void;
  isModalOpen: boolean;
  resource: ApiReturnResource | null;
  tags: ApiTag[] | [];
}

function EditResource({
  isModalOpen = false,
  handleClose = () => {},
  resource,
  tags,
}: EditResourceProps) {
  const [projectTags, setProjectTags] = useState<ApiTag[]>([]);
  const [globalTags, setGlobalTags] = useState<ApiTag[]>([]);

  const navigate = useNavigate();
  const refresh = () => {
    navigate(0);
  };

  // Default form values.
  const defaultValues: ApiResource & { file?: File | null; tags: ApiTag[] } = {
    name: resource ? resource.name : '',
    // FIXME: ApiResource is missing the xslsx type
    type: resource ? (resource.type as ApiResource['type']) : 'text',
    content_type: '',
    resource_type: resource ? resource.resource_type : 'text',
    value: resource ? resource.value : '',
    project_id: resource ? resource.project_id : 0,
    site_id: resource ? resource.site_id : 0,
    file: null,
    tags: resource
      ? resource.tag_names!.map((t) => ({ name: t, is_generic: false }))
      : [],
  };

  // Validator for the form fields.
  const validator = createValidator<
    ApiResource & { file?: File | null; tags: ApiTag[] }
  >({
    project_id: [],
    site_id: [],
    name: [],
    type: [],
    content_type: [],
    resource_type: [],
    value: [],
    file: [],
    tags: [],
  });

  const processEditTags = async (
    prevTags: ApiTag[],
    newTags: ApiTag[],
    globalTags: ApiTag[]
  ): Promise<void> => {
    const deletedTags = prevTags.filter(
      (prevTag) => !includeByName(prevTag, newTags)
    );
    const addedTags = newTags.filter(
      (newTag) => !includeByName(newTag, prevTags)
    );

    deletedTags.length !== 0 &&
      (await TagService.deleteFromResource(
        resource!.id,
        deletedTags.map(({ name }) => name)
      ));

    await Promise.all(
      addedTags.map(async (addedTag) => {
        if (!includeByName(addedTag, globalTags)) {
          // Attendre que le tag soit ajouté avant de l'associer au projet
          await TagService.add(addedTag);
          await TagService.associateTagToProject(
            [addedTag.name],
            resource!.project_id
          );
        }
        // Associer le tag à la ressource
        await TagService.associateTagToResource(resource!.id, [addedTag.name]);
      })
    );
  };

  const onSubmit = async (data: ApiResource) => {
    try {
      processEditTags(tags, methods.watch('tags'), globalTags);
      await resourceService.update(resource!.id, data);

      UIkit.notification({
        message: 'La modification a été faite avec succès',
        status: 'success',
      });

      closeModal();
      setTimeout(refresh, 1000);
    } catch (error: any) {
      console.error('Error:', error);

      UIkit.notification({
        message:
          error.response?.data?.message ||
          'Une erreur est survenue lors de la modification de la ressource.',
        status: 'danger',
      });
      closeModal();
    }
  };

  // Define form hook.
  const methods = useForm(defaultValues, validator, onSubmit);

  // Check for errors on a specific field
  const isErrorOnField = (field: keyof ApiResource): string => {
    return methods.getErrors(field).length > 0
      ? 'form-danger'
      : methods.hasSubmitted
      ? 'form-success'
      : '';
  };

  const handleSelectedTags = (values: ApiTag[]) => {
    methods.setValue('tags', values);
  };

  const closeModal = () => {
    handleClose(isModalOpen);
    methods.reset();
    return true;
  };

  useEffect(() => {
    const fetchAllTags = async () => {
      try {
        const response = await api_config.get(`/tags`);
        return response.data;
      } catch (error) {
        console.error('Failed to fetch selection values:', error);
        return null;
      }
    };

    const fetchProjectTags = async () => {
      if (resource) {
        try {
          const queryParams = `project_id=${resource.project_id}`;
          const response = await api_config.get(`/project_tags?${queryParams}`);
          return response.data;
        } catch (error) {
          console.error('Failed to fetch selection values:', error);
          return null;
        }
      }
    };

    const callback = async () => {
      setGlobalTags(await fetchAllTags());
      setProjectTags(await fetchProjectTags());
      methods.reset();
    };

    callback();
  }, []);

  return (
    <PureModal
      header="Modifier la ressource"
      className="pure-modal-management"
      isOpen={isModalOpen}
      closeButton={<span className="icon" uk-icon="close"></span>}
      closeButtonPosition="header"
      onClose={() => {
        handleClose(isModalOpen);
        methods.reset();
        return true;
      }}
    >
      <form onSubmit={methods.onSubmit} className="form ressource">
        <TextInputComponent
          label="Nom "
          name="name"
          placeholder="Nom de la ressource"
          onChange={(stringValue) => methods.setValue('name', stringValue)}
          required
          className={`${isErrorOnField('name')} uk-margin`}
          errors={
            methods.hasSubmitted
              ? { tag: 'Submited', errors: methods.getErrors('name') }
              : { tag: 'Not submitted' }
          }
        />
        {resource && resource.resource_type === 'text' ? (
          <div className="uk-margin">
            <label htmlFor="form-horizontal-text">Ajouter du texte</label>
            <div className="uk-form-controls">
              <input
                className="uk-input"
                id="form-horizontal-text"
                type="text"
                placeholder="Ajouter du texte"
                onChange={(e) => methods.setValue('value', e.target.value)}
                value={methods.watch('value') as string}
              />
            </div>
          </div>
        ) : (
          <div>
            <FilePicker
              filehandler={(file: File | null) =>
                methods.setValue('file', file)
              }
            />
            {methods.watch('file') === null ? (
              <div className="image-preview">
                <div style={{ textAlign: 'center' }}>
                  <img
                    style={{ textAlign: 'center' }}
                    className="image"
                    src={`data:image/png;base64,${methods.watch('value')}`}
                    alt="Selected file preview"
                  />
                </div>
              </div>
            ) : null}
          </div>
        )}
        <TagInput
          predicate={(substring) => (data) =>
            Object.values(data).some((value) =>
              String(value).toLowerCase().includes(substring[0]!.toLowerCase())
            )}
          selectionValues={
            projectTags
              ? (projectTags as {
                  id: number;
                  name: string;
                  is_generic: boolean;
                }[])
              : []
          }
          handleSelectedValues={handleSelectedTags}
          initialTags={methods.watch('tags')}
        />
        <button type="submit" className="uk-button uk-button-default">
          {'Enregistrer'}
        </button>
      </form>
    </PureModal>
  );
}

export default forwardRef(EditResource);
